import { SUPABASE_URL } from '../../utils/supabase/url';
/**
 * Thumbnail Image Component
 * Loads authenticated bucket previews; shared legacy callers may still use Drive.
 */

import React, { useState, useEffect } from 'react';
import { FileText, Loader2 } from '@/shared/ui/icons';
import { useAuth } from '../../platform/auth/AuthContext';
import { getAuthHeaders } from '../../utils/supabase/client';
import { fetchNativeDocumentThumbnail } from './nativeDocumentThumbnail';
import { getPersistentDocumentThumbnail, type ThumbnailScope } from './documentThumbnailStore';

interface ThumbnailImageProps {
  fileId: string;
  mimeType?: string;
  alt: string;
  projectId?: string;
  modifiedTime?: string;
  revision?: string;
  className?: string;
  /** Drive API thumbnailLink — when provided, used directly on web (skips proxy round-trip) */
  thumbnailLink?: string;
  /** Opt-in: render previews on the native macOS shell too. By default macOS
   *  disables thumbnails entirely (WKWebView can crash decoding some WEBP).
   *  When set, previews still render — but ONLY through the proxy path, which
   *  rejects WEBP by content-type + magic bytes so the crash-prone format never
   *  reaches the decoder. The unguarded direct-thumbnailLink path stays web-only. */
  renderOnMacOS?: boolean;
  /** Documents is bucket-only; legacy shared callers opt into automatic provider selection. */
  storageProvider?: 'bucket' | 'auto';
}

const THUMBNAIL_CACHE_MAX = 200;
const MAX_CONCURRENT_THUMBNAILS = 6;
const FAILED_THUMBNAIL_TTL_MS = 5 * 60 * 1000;
const thumbnailCache = new Map<string, string>();
const failedThumbnailCache = new Map<string, number>();
const inflightThumbnails = new Map<string, Promise<string>>();
const thumbnailQueue: Array<() => void> = [];
let activeThumbnailFetches = 0;
let cacheGeneration = 0;
if (typeof window !== 'undefined') window.addEventListener('quantis:auth-cleared', () => {
  cacheGeneration += 1;
  for (const url of thumbnailCache.values()) URL.revokeObjectURL(url);
  thumbnailCache.clear(); failedThumbnailCache.clear(); inflightThumbnails.clear();
});

const enqueueThumbnailFetch = <T,>(task: () => Promise<T>) =>
  new Promise<T>((resolve, reject) => {
    const run = () => {
      activeThumbnailFetches += 1;
      task()
        .then(resolve)
        .catch(reject)
        .finally(() => {
          activeThumbnailFetches -= 1;
          const next = thumbnailQueue.shift();
          if (next) next();
        });
    };

    if (activeThumbnailFetches < MAX_CONCURRENT_THUMBNAILS) {
      run();
    } else {
      thumbnailQueue.push(run);
    }
  });

const touchThumbnailCache = (cacheKey: string, url: string) => {
  if (thumbnailCache.has(cacheKey)) {
    thumbnailCache.delete(cacheKey);
  }
  thumbnailCache.set(cacheKey, url);

  if (thumbnailCache.size > THUMBNAIL_CACHE_MAX) {
    const oldest = thumbnailCache.keys().next().value;
    if (oldest) {
      const oldUrl = thumbnailCache.get(oldest);
      thumbnailCache.delete(oldest);
      if (oldUrl) {
        URL.revokeObjectURL(oldUrl);
      }
    }
  }
};

const markThumbnailFailure = (cacheKey: string) => {
  failedThumbnailCache.set(cacheKey, Date.now());
};

const hasRecentThumbnailFailure = (cacheKey: string) => {
  const failedAt = failedThumbnailCache.get(cacheKey);
  if (!failedAt) return false;
  if (Date.now() - failedAt > FAILED_THUMBNAIL_TTL_MS) {
    failedThumbnailCache.delete(cacheKey);
    return false;
  }
  return true;
};

/**
 * Returns true if the first 12 bytes of a blob match the WEBP magic signature:
 * bytes 0-3 = "RIFF", bytes 8-11 = "WEBP".
 * Used to reject WEBP data even when the server sends a wrong Content-Type.
 */
const isBlobWebp = async (blob: Blob): Promise<boolean> => {
  try {
    const buf = await blob.slice(0, 12).arrayBuffer();
    const b = new Uint8Array(buf);
    return (
      b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 && // RIFF
      b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50   // WEBP
    );
  } catch {
    return false;
  }
};

const isNativeMacOS = (): boolean => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  if (window.__QUANTIS_NATIVE__ !== true) return false;
  return document.documentElement.getAttribute('data-quantis-native-platform') === 'macos';
};

const verifyImageDecodes = (url: string) =>
  new Promise<void>((resolve, reject) => {
    const img = new Image();
    // Do NOT use async decoding on native macOS — concurrent off-thread WEBP
    // decode failures can crash the WKWebView WebContent process.
    if (!isNativeMacOS()) {
      img.decoding = 'async';
    }
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('Thumbnail decode failed'));
    img.src = url;
  });

const fetchThumbnailUrl = async (fileId: string, userId: string, scope: ThumbnailScope, mimeType?: string) => {
  if (fileId.startsWith('native-')) {
    const blob = await getPersistentDocumentThumbnail(scope, () => fetchNativeDocumentThumbnail(fileId, mimeType));
    if (blob.type.includes('image/webp') || await isBlobWebp(blob)) {
      throw new Error('WEBP thumbnails are not supported in this runtime');
    }
    const url = URL.createObjectURL(blob);
    try { await verifyImageDecodes(url); return url; }
    catch (error) { URL.revokeObjectURL(url); throw error; }
  }
  const proxyUrl = `${SUPABASE_URL}/functions/v1/make-server-a476273b/files/thumbnail/${fileId}`;
  const response = await fetch(proxyUrl, {
    headers: await getAuthHeaders({ 'X-User-Id': userId }),
  });

  if (!response.ok) {
    throw new Error(`Failed to load thumbnail (HTTP ${response.status})`);
  }

  const contentType = response.headers.get('Content-Type')?.toLowerCase() ?? '';
  if (!contentType.startsWith('image/')) {
    throw new Error(`Invalid thumbnail content type: ${contentType || 'unknown'}`);
  }
  // WKWebView on macOS has recurring decode failures for some WEBP thumbnails.
  // Fall back to file icon instead of repeatedly attempting to decode them.
  if (contentType.includes('image/webp')) {
    throw new Error('WEBP thumbnails are not supported in this runtime');
  }

  const blob = await response.blob();

  // Some servers misreport the Content-Type for WEBP files (e.g., sending
  // image/jpeg for actual WEBP data). Detect via magic bytes to prevent
  // WKWebView from attempting to decode them — concurrent async WEBP decode
  // failures can crash the WebContent process on macOS native.
  if (await isBlobWebp(blob)) {
    throw new Error('WEBP thumbnails are not supported in this runtime');
  }

  const objectUrl = URL.createObjectURL(blob);

  try {
    await verifyImageDecodes(objectUrl);
    return objectUrl;
  } catch (error) {
    URL.revokeObjectURL(objectUrl);
    throw error;
  }
};

const getThumbnailUrl = (fileId: string, userId: string, scope: ThumbnailScope, mimeType?: string) => {
  const cacheKey = JSON.stringify([SUPABASE_URL, userId, scope.projectId, fileId, scope.modifiedTime, scope.revision]);
  const started = cacheGeneration;
  if (hasRecentThumbnailFailure(cacheKey)) {
    return Promise.reject(new Error('Recent thumbnail decode failure'));
  }

  const cached = thumbnailCache.get(cacheKey);
  if (cached) {
    touchThumbnailCache(cacheKey, cached);
    return Promise.resolve(cached);
  }

  const existing = inflightThumbnails.get(cacheKey);
  if (existing) {
    return existing;
  }

  const promise = enqueueThumbnailFetch(async () => {
    try {
      if (started !== cacheGeneration) throw new Error('Preview session ended');
      const url = await fetchThumbnailUrl(fileId, userId, scope, mimeType);
      if (started !== cacheGeneration) { URL.revokeObjectURL(url); throw new Error('Preview session ended'); }
      touchThumbnailCache(cacheKey, url);
      failedThumbnailCache.delete(cacheKey);
      return url;
    } catch (error) {
      if (started === cacheGeneration) markThumbnailFailure(cacheKey);
      throw error;
    }
  }).finally(() => {
    if (started === cacheGeneration) inflightThumbnails.delete(cacheKey);
  });

  inflightThumbnails.set(cacheKey, promise);
  return promise;
};

export function ThumbnailImage({ fileId, mimeType, alt, className, thumbnailLink, projectId = '', modifiedTime = '', revision = '', renderOnMacOS = false, storageProvider = 'auto' }: ThumbnailImageProps) {
  const { user } = useAuth();
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Direct thumbnailLinks are SIGNED Google CDN URLs with short lifetimes,
  // and list data is served from the local cache — a stale listing hands out
  // expired links that fail en masse (observed live: every card iconified).
  // When the direct link errors, retry once through the proxy, which mints a
  // fresh link from live Drive metadata.
  const [directLinkFailed, setDirectLinkFailed] = useState(false);
  const onMac = isNativeMacOS();

  useEffect(() => {
    if (!fileId || !user?.id || (storageProvider === 'bucket' && !fileId.startsWith('native-file:'))) {
      setLoading(false);
      setError(true);
      setThumbnailUrl(null);
      return;
    }

    // macOS default: disable thumbnails (WKWebView WEBP-decode crashes). Opt-in
    // callers (renderOnMacOS) still get previews, but only via the WEBP-guarded
    // proxy path below — the unguarded direct thumbnailLink is skipped on macOS.
    if (onMac && !renderOnMacOS) {
      setLoading(false);
      setError(true);
      setThumbnailUrl(null);
      return;
    }

    // If the list response already included a thumbnailLink, use it directly —
    // no proxy round-trip needed. The URL is a signed CDN link accessible without auth.
    // Upscale: Drive returns =s220 by default; replace with =s1600 for sharp retina display.
    // WEB ONLY: this path has no WEBP guard, so on macOS we fall through to the
    // proxy (which rejects WEBP) instead.
    if (storageProvider !== 'bucket' && thumbnailLink && !fileId.startsWith('native-') && !onMac && !directLinkFailed) {
      setThumbnailUrl(thumbnailLink.replace(/=s\d+$/, '=s1600'));
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setError(false);
    setThumbnailUrl(null);

    getThumbnailUrl(fileId, user.id, { userId: user.id, projectId, fileId, modifiedTime, revision }, mimeType)
      .then((url) => {
        if (!active) return;
        setThumbnailUrl(url);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [fileId, mimeType, user?.id, projectId, modifiedTime, revision, thumbnailLink, onMac, renderOnMacOS, directLinkFailed, storageProvider]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-muted/40 ${className}`}>
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !thumbnailUrl || (storageProvider === 'bucket' && !fileId.startsWith('native-file:'))) {
    return (
      <div className={`flex items-center justify-center bg-muted/40 ${className}`}>
        <FileText className="w-8 h-8 sm:w-12 sm:h-12 text-brand" />
      </div>
    );
  }

  return (
    <img
      src={thumbnailUrl}
      alt={alt}
      className={className}
      onError={() => {
        if (storageProvider !== 'bucket' && thumbnailLink && !onMac && !directLinkFailed) {
          // Expired signed link: flip to the proxy path (the effect re-runs
          // on directLinkFailed) instead of giving up on the preview.
          setDirectLinkFailed(true);
          setThumbnailUrl(null);
          setLoading(true);
          return;
        }
        setError(true);
      }}
    />
  );
}
